import cx from "classnames";
import { ReactNode, useId } from "react";
import capitalize from "lodash-es/capitalize";
import lowerCase from "lodash-es/lowerCase";

import Box, { BoxProps } from "ds/components/Box";
import { Spinner } from "components/Spinner";
import Icon from "ds/components/Icon";
import { withTestId } from "utils/withTestId";
import Timestamp from "components/Timestamp";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { RunContext } from "components/Run/Context";
import { TreeItem, TreeItemContent, TreeItemToggle } from "ds/components/Tree";

import styles from "./styles.module.css";
import State from "../HistoryEntryState";
import Separator from "./Separator";
import HistoryEntryDuration from "../HistoryEntryDuration";
import { RunHistoryEntryType } from "../../types";
import HistoryEntryLabel from "../HistoryEntryLabel";
import {
  RUN_HISTORY_ENTRY_CONTENT_TEST_ID,
  RUN_HISTORY_ENTRY_INFO_TEST_ID,
  RUN_HISTORY_ENTRY_LABEL_TEST_ID,
  RUN_HISTORY_ENTRY_TEST_ID,
} from "./constants";

type HistoryEntryWrapperProps = Omit<BoxProps, "children"> & {
  children?: ReactNode;
  state: RunHistoryEntryType;
  isOpen?: boolean;
  isLoading?: boolean;
  timestamp?: number;
  until?: number | null;
  label?: ReactNode;
  onClick?: () => void;
  terminal?: boolean;
};

const containersNotSupported = () => {
  // is safari
  if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) return true;
  // firefox before version 133.0
  const version = navigator.userAgent.match(/firefox\/(\d+)/i)?.[1];
  return version && parseInt(version) < 133;
};

const HistoryEntryWrapper = ({
  children,
  state,
  isOpen,
  onClick,
  isLoading,
  label,
  timestamp,
  until,
  terminal,
  ...rest
}: HistoryEntryWrapperProps) => {
  const labelId = useId();
  const contentId = useId();
  const trackSegmentAnalyticsEvent = useAnalytics();
  const {
    run: { state: runState, forceApplyMode },
  } = useTypedContext(RunContext);

  const handleToggle = () => {
    if (!onClick) return;

    trackSegmentAnalyticsEvent("Run Logs - toggle logs stage", {
      runState,
      logsState: state,
      isExpanded: !isOpen, // state after toggling
    });
    onClick();
  };

  const isForceApply = state === RunHistoryEntryType.Applying && !!forceApplyMode;

  label ??= (
    <HistoryEntryLabel>
      <strong>{isForceApply ? "Force applying" : capitalize(lowerCase(state))}</strong>
    </HistoryEntryLabel>
  );

  const content = (
    <>
      <span
        id={labelId}
        className={styles.labelWrapper}
        {...withTestId(RUN_HISTORY_ENTRY_LABEL_TEST_ID)}
      >
        {label}
      </span>
      {isLoading && (
        <Icon size="large" src={Spinner} className={styles.spinner} aria-label="Loading" />
      )}
      {!terminal && timestamp && (
        <>
          <Separator />
          <HistoryEntryDuration start={timestamp} end={until || null} />
        </>
      )}
      {timestamp && (
        <>
          <Separator />
          <Timestamp color="secondary" timestamp={timestamp} />
        </>
      )}
    </>
  );

  return (
    <li className={styles.wrapper} {...withTestId(RUN_HISTORY_ENTRY_TEST_ID)}>
      <State type={state} className={styles.state} />
      <TreeItem
        padding="0"
        {...withTestId(RUN_HISTORY_ENTRY_INFO_TEST_ID)}
        className={styles.info}
        autoHeight
      >
        {onClick && (
          <TreeItemToggle
            isExpanded={isOpen}
            onPress={handleToggle}
            aria-labelledby={labelId}
            aria-expanded={isOpen}
            aria-controls={contentId}
          >
            {isOpen ? "Collapse" : "Expand"}
          </TreeItemToggle>
        )}
        <TreeItemContent gap="0">{content}</TreeItemContent>
      </TreeItem>
      {children && (
        <Box
          id={contentId}
          direction="column"
          align="start"
          gap="medium"
          fullWidth
          className={cx(styles.content, { [styles.contentSafari]: containersNotSupported() })}
          {...withTestId(RUN_HISTORY_ENTRY_CONTENT_TEST_ID)}
          {...rest}
        >
          {children}
        </Box>
      )}
    </li>
  );
};

export default HistoryEntryWrapper;
